import { useMemo } from "react";
import { Activity, Check, CircleDashed, HeartPulse, Rocket, ShieldAlert, TriangleAlert, X } from "lucide-react";

import { ProjectIcon } from "../../components/ProjectIcon.jsx";
import { useNow } from "../../lib/useNow.js";
import { DEPLOY_TONE, elapsed, since, took, useIconUrl, useTween } from "./board.js";

/**
 * The pieces every layout is made of.
 *
 * One colour per state and nothing else in colour: green answers, red does
 * not, amber is on its way somewhere, blue is happening now, grey is not
 * known. Everything else on the wall is white on near-black, so that when a
 * thing turns red it is the only thing that changed.
 */

export const TONE = {
    up: { text: "text-emerald-400", bg: "bg-emerald-400", soft: "bg-emerald-400/15", border: "border-emerald-400/30", glow: "shadow-[0_0_2.5rem_-0.5rem_rgba(52,211,153,0.55)]", stroke: "#34d399" },
    down: { text: "text-red-400", bg: "bg-red-500", soft: "bg-red-500/15", border: "border-red-500/40", glow: "shadow-[0_0_2.5rem_-0.5rem_rgba(239,68,68,0.7)]", stroke: "#f87171" },
    failing: { text: "text-amber-400", bg: "bg-amber-400", soft: "bg-amber-400/15", border: "border-amber-400/30", glow: "shadow-[0_0_2.5rem_-0.5rem_rgba(251,191,36,0.55)]", stroke: "#fbbf24" },
    running: { text: "text-sky-400", bg: "bg-sky-400", soft: "bg-sky-400/15", border: "border-sky-400/30", glow: "shadow-[0_0_2.5rem_-0.5rem_rgba(56,189,248,0.6)]", stroke: "#38bdf8" },
    unknown: { text: "text-gray-500", bg: "bg-gray-500", soft: "bg-gray-500/15", border: "border-white/10", glow: "", stroke: "#6b7280" }
};

// A component being restarted is in hand, not on fire: the running blue.
TONE.restarting = TONE.running;

export const toneOf = (state) => TONE[state] ?? TONE.unknown;

/** One word for the board, and the colour it is said in. */
export function headlineOf(board) {
    if (!board) return { text: "CONNECTING", tone: "unknown" };
    if (board.degraded) return { text: "DATABASE UNAVAILABLE", tone: "failing" };
    const { state, down } = board.status;
    if (state === "down") return { text: `${down} NOT ANSWERING`, tone: "down" };
    if (state === "unknown") return { text: "NOT CHECKED YET", tone: "unknown" };
    return { text: "ALL SYSTEMS OPERATIONAL", tone: "up" };
}

// --- atoms ----------------------------------------------------------------------------------

/** A dot with rings leaving it: alive when green, urgent when red. */
export function Orb({ tone = "unknown", size = "2.5rem", className = "" }) {
    const t = toneOf(tone);
    const urgent = tone === "down";
    const still = tone === "unknown";
    return (
        <span className={`relative inline-flex shrink-0 ${className}`} style={{ width: size, height: size }} aria-hidden>
            {!still ? <span className={`absolute inset-0 rounded-full ${t.bg} opacity-40 ${urgent ? "animate-ring-fast" : "animate-ring"}`} /> : null}
            {!still ? <span className={`absolute inset-0 rounded-full ${t.bg} opacity-40 ${urgent ? "animate-ring-fast [animation-delay:0.65s]" : "animate-ring [animation-delay:1.3s]"}`} /> : null}
            <span className={`absolute inset-[26%] rounded-full ${t.bg} ${t.glow}`} />
        </span>
    );
}

/** A small state dot, breathing while up, urgent while down. */
export function Dot({ tone = "unknown", className = "size-2.5" }) {
    const t = toneOf(tone);
    const motion = tone === "down" ? "animate-ring-fast" : tone === "running" ? "animate-ring" : "";
    return (
        <span className={`relative inline-flex shrink-0 rounded-full ${t.bg} ${className}`} aria-hidden>
            {motion ? <span className={`absolute inset-0 rounded-full ${t.bg} opacity-50 ${motion}`} /> : null}
        </span>
    );
}

/** A number that is seen to change. */
export function Count({ value, decimals = 0, suffix = "", className = "" }) {
    const shown = useTween(typeof value === "number" ? value : 0);
    return (
        <span className={`live-num ${className}`}>
            {typeof value === "number" ? shown.toFixed(decimals) : "—"}
            {suffix}
        </span>
    );
}

export function Clock({ className = "" }) {
    const now = useNow();
    const date = new Date(now);
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");
    return (
        <div className={`text-right ${className}`}>
            <div className="live-num text-[2.4rem] font-light leading-none tracking-tight text-gray-100">
                {hh}
                <span className="animate-breathe text-gray-500">:</span>
                {mm}
                <span className="text-[0.5em] text-gray-500"> {ss}</span>
            </div>
            <div className="live-label mt-1.5">
                {date.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
            </div>
        </div>
    );
}

/** The mark, and where this is. */
export function Brand({ sub = "live" }) {
    return (
        <div className="flex items-center gap-3">
            <img src="/logo-white.svg" alt="scaling-ops" className="h-[1.6rem]" />
            <span className="text-[1.1rem] font-semibold tracking-tight text-gray-400">ops</span>
            <span className="live-label ml-1 rounded-full border border-white/10 px-2 py-0.5">{sub}</span>
        </div>
    );
}

/** A project's icon, fetched with the screen key when there is one. */
export function LiveIcon({ project, size = 28, screenKey, className = "" }) {
    const src = useIconUrl(project?.slug, project?.faviconHash, screenKey);
    return <ProjectIcon slug={project?.slug} hash={project?.faviconHash} src={src} size={size} framed className={className} />;
}

export function Label({ children, className = "" }) {
    return <div className={`live-label ${className}`}>{children}</div>;
}

// --- figures --------------------------------------------------------------------------------

export function Kpi({ label, value, sub, tone = "unknown", decimals = 0, suffix = "" }) {
    const t = toneOf(tone);
    return (
        <div className="live-card flex flex-col justify-between gap-1 px-[1.2rem] py-[0.9rem]">
            <Label>{label}</Label>
            <div className={`text-[2.1rem] font-light leading-none ${tone === "unknown" ? "text-gray-100" : t.text}`}>
                <Count value={value} decimals={decimals} suffix={suffix} />
            </div>
            {sub ? <div className="truncate text-[0.85rem] text-gray-500">{sub}</div> : null}
        </div>
    );
}

/** A three-quarter arc that fills as a resource does. */
export function Gauge({ label, ratio, text, sub, tone, trace }) {
    const r = 42;
    const circumference = 2 * Math.PI * r;
    const arc = circumference * 0.75;
    const clamped = Math.max(0, Math.min(1, ratio ?? 0));
    const shown = useTween(clamped, 900);
    const t = toneOf(tone ?? (clamped > 0.9 ? "down" : clamped > 0.75 ? "failing" : "up"));
    return (
        <div className="live-card flex items-center gap-[1rem] px-[1rem] py-[0.7rem]">
            <div className="relative aspect-square w-[5.6rem] shrink-0">
                <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden>
                    <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" strokeLinecap="round" strokeDasharray={`${arc} ${circumference}`} transform="rotate(135 50 50)" />
                    <circle cx="50" cy="50" r={r} fill="none" stroke={t.stroke} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${arc * shown} ${circumference}`} transform="rotate(135 50 50)" style={{ filter: `drop-shadow(0 0 6px ${t.stroke}88)` }} />
                </svg>
                <div className={`absolute inset-0 flex items-center justify-center live-num text-[1.35rem] font-light ${t.text}`}>{text}</div>
            </div>
            <div className="min-w-0 flex-1">
                <Label>{label}</Label>
                {sub ? <div className="mt-1 truncate text-[0.9rem] text-gray-400">{sub}</div> : null}
                {trace ? <Trace values={trace.values} max={trace.max} tone={t === TONE.up ? "running" : tone ?? "running"} className="mt-2 h-[2.2rem]" /> : null}
            </div>
        </div>
    );
}

/** A line of the last fifteen minutes, filled beneath. */
export function Trace({ values = [], max = null, tone = "running", className = "h-[3rem]" }) {
    const t = toneOf(tone);
    const known = values.filter((value) => value !== null && value !== undefined);
    if (known.length < 2) return <div className={`${className} w-full`} />;
    const top = max ?? Math.max(...known, 1);
    const step = 100 / (values.length - 1);
    const points = values.map((value, i) => (value === null || value === undefined ? null : [i * step, 100 - (value / top) * 100]));
    const path = points.reduce((d, point, i) => {
        if (!point) return d;
        const previous = points[i - 1];
        return `${d}${previous ? "L" : "M"}${point[0].toFixed(2)},${point[1].toFixed(2)} `;
    }, "");
    const area = points.filter(Boolean);
    const fill = area.length ? `M${area[0][0].toFixed(2)},100 ${area.map(([x, y]) => `L${x.toFixed(2)},${y.toFixed(2)}`).join(" ")} L${area.at(-1)[0].toFixed(2)},100 Z` : "";
    return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={`${className} w-full`} aria-hidden>
            <path d={fill} fill={t.stroke} opacity="0.12" />
            <path d={path} fill="none" stroke={t.stroke} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
    );
}

/** A slim bar with its figure — a gauge where there is no room for one. */
export function Bar({ label, ratio, text }) {
    const clamped = Math.max(0, Math.min(1, ratio ?? 0));
    const shown = useTween(clamped, 900);
    const t = toneOf(clamped > 0.9 ? "down" : clamped > 0.75 ? "failing" : "up");
    return (
        <div className="min-w-0">
            <div className="flex items-baseline justify-between gap-2">
                <Label>{label}</Label>
                <span className={`live-num text-[0.95rem] ${t.text}`}>{text}</span>
            </div>
            <div className="mt-1.5 h-[0.4rem] overflow-hidden rounded-full bg-white/10">
                <div className={`h-full rounded-full ${t.bg}`} style={{ width: `${shown * 100}%` }} />
            </div>
        </div>
    );
}

// --- deployments ----------------------------------------------------------------------------

const STEP_ICON = {
    success: (cls) => <Check className={cls} strokeWidth={3} />,
    failed: (cls) => <X className={cls} strokeWidth={3} />,
    skipped: (cls) => <CircleDashed className={cls} />
};

/** Steps as stations on a line; the running one is lit. */
export function StepTrack({ steps = [], big = false }) {
    const node = big ? "size-[2.2rem]" : "size-[1.4rem]";
    const icon = big ? "size-[1.1rem]" : "size-[0.75rem]";
    return (
        <ol className="flex items-start">
            {steps.map((step, i) => {
                const tone = step.status === "success" ? "up" : step.status === "failed" ? "down" : step.status === "running" ? "running" : "unknown";
                const t = toneOf(tone);
                const reached = step.status !== "pending";
                return (
                    <li key={step.name} className="flex min-w-0 flex-1 items-start">
                        <div className="flex min-w-0 flex-col items-center gap-1.5">
                            <span className={`relative flex shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-500 ${node} ${
                                reached ? `${t.border} ${t.soft} ${t.text}` : "border-white/15 text-gray-600"
                            }`}>
                                {step.status === "running" ? <span className={`absolute inset-0 rounded-full ${t.bg} opacity-40 animate-ring`} /> : null}
                                {step.status === "running" ? <span className={`rounded-full ${t.bg} ${big ? "size-[0.7rem]" : "size-[0.45rem]"}`} /> : STEP_ICON[step.status]?.(icon) ?? null}
                            </span>
                            <span className={`max-w-full truncate text-center ${big ? "text-[0.9rem]" : "text-[0.7rem]"} ${reached ? "text-gray-200" : "text-gray-600"}`}>{step.name}</span>
                        </div>
                        {i < steps.length - 1 ? (
                            <span className={`mt-[calc(var(--node)/2)] h-0.5 flex-1 transition-colors duration-700 ${step.status === "success" ? "bg-emerald-400/60" : "bg-white/10"}`} style={{ "--node": big ? "2.2rem" : "1.4rem" }} />
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

/** How far along, as a bar that shimmers while it moves. */
export function Progress({ steps = [], status }) {
    const total = steps.length || 1;
    const done = steps.filter((step) => step.status === "success" || step.status === "skipped").length;
    const running = steps.some((step) => step.status === "running") ? 0.5 : 0;
    const ratio = status === "success" ? 1 : Math.min(1, (done + running) / total);
    const shown = useTween(ratio, 900);
    const tone = status === "failed" || status === "rolled-back" ? "down" : status === "success" ? "up" : "running";
    const t = toneOf(tone);
    return (
        <div className="h-[0.45rem] overflow-hidden rounded-full bg-white/10">
            <div className={`relative h-full rounded-full ${t.bg} ${t.glow}`} style={{ width: `${Math.max(3, shown * 100)}%` }}>
                {status === "running" ? <span className="live-shimmer" /> : null}
            </div>
        </div>
    );
}

const LEVEL_CLASS = {
    step: "text-sky-300 font-semibold",
    err: "text-red-300",
    fail: "text-red-300",
    warn: "text-amber-300",
    ok: "text-emerald-300",
    info: "text-gray-400",
    out: "text-gray-300"
};

/** The end of a log, newest at the bottom, each new line arriving rather than appearing. */
export function LogTail({ lines = [], rows = 8, className = "" }) {
    const keyed = useMemo(() => {
        const seen = new Map();
        return lines.slice(-rows).map((line) => {
            const base = `${line.t ?? ""}|${line.text}`;
            const n = (seen.get(base) ?? 0) + 1;
            seen.set(base, n);
            return { ...line, key: n > 1 ? `${base}#${n}` : base };
        });
    }, [lines, rows]);

    return (
        <div className={`live-num overflow-hidden text-[0.82rem] leading-[1.5] ${className}`}>
            {keyed.length === 0 ? <div className="text-gray-600">waiting for output…</div> : null}
            {keyed.map((line) => (
                <div key={line.key} className="flex gap-3 truncate animate-rise">
                    <span className="shrink-0 text-gray-600">{line.t ? new Date(line.t).toLocaleTimeString(undefined, { hour12: false }) : "        "}</span>
                    <span className={`truncate ${LEVEL_CLASS[line.level] ?? LEVEL_CLASS.out}`}>{line.level === "step" ? `── ${line.text}` : line.text}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * A deployment in progress. `big` is the stage version, `compact` is one waiting
 * its turn — a single line, so the one that is running keeps the room.
 */
export function DeployCard({ deployment, project, screenKey, big = false, compact = false, rows = big ? 10 : 5, siblings = [] }) {
    const now = useNow();
    const running = deployment.status === "running";
    const tone = DEPLOY_TONE[deployment.status] ?? "unknown";
    const t = toneOf(tone);
    const icon = project ?? { slug: deployment.slug, faviconHash: null };
    // A follower already queued on the board needs no announcing.
    const chain = (deployment.chain ?? []).filter((slug) => !siblings.some((other) => other.slug === slug));

    if (compact) {
        return (
            <div className={`live-card flex shrink-0 items-center gap-3 px-[0.9rem] py-[0.6rem] animate-rise ${t.border}`}>
                <LiveIcon project={icon} size={26} screenKey={screenKey} />
                <span className="truncate font-semibold text-gray-100">{deployment.slug}</span>
                <span className="live-num text-gray-500">#{deployment.number}</span>
                <span className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${t.soft} ${t.text}`}>
                    <Dot tone={tone} className="size-1.5" />
                    {deployment.status}
                </span>
                <span className="min-w-0 flex-1 truncate text-[0.8rem] text-gray-500">{deployment.commit.message}</span>
                <span className="live-num shrink-0 text-[0.9rem] text-gray-500">{elapsed(deployment.startedAt ?? deployment.queuedAt, now)}</span>
            </div>
        );
    }

    return (
        <div className={`live-card shrink-0 animate-rise ${big ? "p-[1.4rem]" : "p-[1rem]"} ${t.border} ${running ? t.glow : ""}`}>
            <div className="flex items-center gap-[1rem]">
                <LiveIcon project={icon} size={big ? 64 : 40} screenKey={screenKey} />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className={`truncate font-semibold tracking-tight ${big ? "text-[2.2rem] leading-none" : "text-[1.25rem]"}`}>{deployment.slug}</span>
                        <span className={`live-num ${big ? "text-[1.4rem]" : "text-[1rem]"} text-gray-400`}>#{deployment.number}</span>
                        <span className={`inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.15em] ${t.soft} ${t.text}`}>
                            <Dot tone={tone} className="size-1.5" />
                            {running ? "deploying" : deployment.status}
                        </span>
                    </div>
                    <div className={`mt-1 truncate text-gray-400 ${big ? "text-[1.05rem]" : "text-[0.85rem]"}`}>
                        <span className="live-num text-gray-500">{deployment.branch ?? "main"} · {deployment.commit.shortSha ?? "—"}</span>
                        {deployment.commit.message ? <span className="text-gray-200"> — {deployment.commit.message}</span> : null}
                    </div>
                </div>
                <div className="shrink-0 text-right">
                    <div className={`live-num font-light leading-none ${big ? "text-[3.2rem]" : "text-[1.6rem]"} ${running ? "text-gray-100" : "text-gray-500"}`}>
                        {elapsed(deployment.startedAt ?? deployment.queuedAt, now)}
                    </div>
                    <Label className="mt-1">{running ? "elapsed" : deployment.status}</Label>
                </div>
            </div>

            <div className={big ? "mt-[1.4rem]" : "mt-[1rem]"}>
                <Progress steps={deployment.steps} status={deployment.status} />
            </div>
            <div className={big ? "mt-[1.4rem]" : "mt-[1rem]"}>
                <StepTrack steps={deployment.steps} big={big} />
            </div>
            <div className={`mt-[0.9rem] rounded-xl bg-black/40 ${big ? "p-[1rem]" : "px-[0.7rem] py-[0.5rem]"}`}>
                <LogTail lines={deployment.tail} rows={rows} />
            </div>
            {chain.length ? <div className="mt-1.5 text-[0.8rem] text-gray-500">then {chain.join(" → ")}</div> : null}
        </div>
    );
}

/** A deployment that is over, on one line. */
export function DeployLine({ deployment, project, screenKey, now, className = "" }) {
    const tone = DEPLOY_TONE[deployment.status] ?? "unknown";
    const t = toneOf(tone);
    return (
        <div className={`flex min-w-0 items-center gap-3 ${className}`}>
            <LiveIcon project={project ?? { slug: deployment.slug, faviconHash: null }} size={26} screenKey={screenKey} />
            <span className="truncate font-medium text-gray-200">{deployment.slug}</span>
            <span className="live-num text-gray-500">#{deployment.number}</span>
            <span className={`${t.text} text-[0.8rem] font-semibold uppercase tracking-[0.12em]`}>{deployment.status}</span>
            <span className="live-num text-[0.85rem] text-gray-500">{took(deployment.durationMs)}</span>
            <span className="live-num text-[0.85rem] text-gray-600">{since(deployment.endedAt, now)}</span>
        </div>
    );
}

/** Recent deployments, sliding by. Two copies, so the loop has no seam. */
export function Ticker({ deployments = [], projects, screenKey }) {
    const now = useNow(10_000);
    if (deployments.length === 0) return <div className="live-label px-2">no deployments yet</div>;
    const items = [...deployments, ...deployments];
    return (
        <div className="overflow-hidden whitespace-nowrap [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]">
            <div className="inline-flex w-max animate-marquee">
                {items.map((deployment, i) => (
                    <DeployLine key={`${deployment.id}-${i}`} deployment={deployment} project={projects?.get(deployment.slug)} screenKey={screenKey} now={now} className="pr-[3.5rem]" />
                ))}
            </div>
        </div>
    );
}

// --- incidents ------------------------------------------------------------------------------

export function IncidentCard({ incident, big = false }) {
    const now = useNow();
    const t = TONE.down;
    return (
        <div className={`live-card animate-rise border-l-4 ${t.border} border-l-red-500 ${t.glow} ${big ? "p-[1.4rem]" : "p-[1rem]"}`}>
            <div className="flex items-start gap-3">
                <TriangleAlert className={`mt-0.5 shrink-0 ${t.text} ${big ? "size-[1.8rem]" : "size-[1.2rem]"}`} />
                <div className="min-w-0 flex-1">
                    <div className={`truncate font-semibold ${big ? "text-[1.8rem] leading-tight" : "text-[1.1rem]"}`}>{incident.name}</div>
                    <div className={`truncate text-gray-400 ${big ? "text-[1rem]" : "text-[0.85rem]"}`}>
                        {incident.group} · {incident.reason || "not answering"}
                    </div>
                </div>
                <div className="shrink-0 text-right">
                    <div className={`live-num font-light leading-none ${t.text} ${big ? "text-[2.4rem]" : "text-[1.4rem]"}`}>{elapsed(incident.startedAt, now)}</div>
                    <Label className="mt-1">down for</Label>
                </div>
            </div>
        </div>
    );
}

// --- the fleet ------------------------------------------------------------------------------

export function ProjectTile({ project, screenKey, now }) {
    const tone = project.paused ? "unknown" : project.monitor;
    const t = toneOf(tone);
    const last = project.lastDeployment;
    const lastTone = last ? DEPLOY_TONE[last.status] ?? "unknown" : "unknown";
    return (
        <div className={`live-card flex flex-col gap-[0.7rem] p-[1rem] transition-[border-color,box-shadow] duration-700 ${tone === "down" ? `${t.border} ${t.glow}` : ""}`}>
            <div className="flex items-center gap-3">
                <LiveIcon project={project} size={34} screenKey={screenKey} />
                <div className="min-w-0 flex-1">
                    <div className="truncate text-[1.05rem] font-semibold tracking-tight">{project.name}</div>
                    <div className="truncate text-[0.75rem] text-gray-500">{project.paused ? "paused" : project.state === "draft" ? "draft" : project.slug}</div>
                </div>
                <Dot tone={tone} className="size-3" />
            </div>
            <div className="flex items-center justify-between gap-2 text-[0.8rem]">
                {last ? (
                    <span className="min-w-0 truncate">
                        <span className="live-num text-gray-500">#{last.number}</span>{" "}
                        <span className={toneOf(lastTone).text}>{last.status}</span>{" "}
                        <span className="live-num text-gray-600">{since(last.at, now)}</span>
                    </span>
                ) : (
                    <span className="text-gray-600">never deployed</span>
                )}
                <span className="live-num shrink-0 text-gray-600">
                    {project.components.length ? `${project.components.filter((c) => c.state === "up").length}/${project.components.length}` : ""}
                </span>
            </div>
            {project.components.length ? (
                <div className="flex flex-wrap gap-1.5">
                    {project.components.map((component) => (
                        <span key={component.name} title={component.name} className={`h-[0.45rem] flex-1 rounded-full transition-colors duration-700 ${toneOf(component.state).bg} ${component.state === "up" ? "opacity-70" : ""}`} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

/** The fleet as a compact list: a row per project. */
export function ProjectRow({ project, screenKey, now }) {
    const tone = project.paused ? "unknown" : project.monitor;
    const last = project.lastDeployment;
    return (
        <div className={`flex items-center gap-3 rounded-xl px-2 py-[0.45rem] transition-colors duration-700 ${tone === "down" ? "bg-red-500/10" : ""}`}>
            <Dot tone={tone} className="size-2.5" />
            <LiveIcon project={project} size={24} screenKey={screenKey} />
            <span className="min-w-0 flex-1 truncate text-[0.95rem] font-medium text-gray-200">{project.name}</span>
            {last ? (
                <span className="live-num shrink-0 text-[0.75rem] text-gray-500">
                    <span className={toneOf(DEPLOY_TONE[last.status] ?? "unknown").text}>#{last.number}</span> {since(last.at, now)}
                </span>
            ) : null}
        </div>
    );
}

/** Every component of every project as one square each: the box, at a glance. */
export function Matrix({ projects = [], screenKey }) {
    return (
        <div className="flex flex-col gap-[0.6rem]">
            {projects.map((project) => (
                <div key={project.slug} className="flex items-center gap-3">
                    <LiveIcon project={project} size={24} screenKey={screenKey} />
                    <span className="w-[9rem] shrink-0 truncate text-[0.9rem] text-gray-300">{project.name}</span>
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                        {project.components.length === 0 ? <span className="text-[0.75rem] text-gray-600">nothing watched</span> : null}
                        {project.components.map((component) => {
                            const t = toneOf(project.paused ? "unknown" : component.state);
                            return (
                                <span
                                    key={component.name}
                                    title={`${component.name} · ${component.state}`}
                                    className={`relative size-[1.35rem] rounded-[0.35rem] transition-colors duration-700 ${t.bg} ${component.state === "up" ? "animate-breathe" : ""}`}
                                >
                                    {component.state === "down" ? <span className={`absolute inset-0 rounded-[0.3rem] ${t.bg} opacity-60 animate-ring-fast`} /> : null}
                                </span>
                            );
                        })}
                    </div>
                    {project.down > 0 ? <span className="live-num shrink-0 text-[0.8rem] text-red-400">{project.down} down</span> : null}
                </div>
            ))}
        </div>
    );
}

// --- the stream -----------------------------------------------------------------------------

const FEED_ICON = {
    deploy: Rocket,
    incident: ShieldAlert,
    recovered: HeartPulse,
    certificate: TriangleAlert
};

export function Feed({ events = [], limit = 12, projects, screenKey, big = false }) {
    const now = useNow(10_000);
    return (
        <ol className="flex flex-col gap-[0.5rem]">
            {events.length === 0 ? <li className="live-label px-2">nothing has happened yet</li> : null}
            {events.slice(0, limit).map((event) => {
                const t = toneOf(event.tone);
                const Icon = FEED_ICON[event.kind] ?? Activity;
                const project = projects?.get(event.title.split(" ")[0]);
                return (
                    <li key={event.id} className={`live-card flex items-center gap-3 animate-rise ${big ? "p-[0.9rem]" : "p-[0.7rem]"} ${event.live ? t.border : ""}`}>
                        <span className={`flex shrink-0 items-center justify-center rounded-xl ${t.soft} ${t.text} ${big ? "size-[2.6rem]" : "size-[2rem]"}`}>
                            {event.kind === "deploy" && project ? <LiveIcon project={project} size={big ? 26 : 20} screenKey={screenKey} /> : <Icon className={big ? "size-[1.3rem]" : "size-[1rem]"} />}
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className={`flex items-center gap-2 ${big ? "text-[1.05rem]" : "text-[0.9rem]"}`}>
                                <span className="truncate font-semibold text-gray-100">{event.title}</span>
                                {event.live ? <Dot tone={event.tone} className="size-2" /> : null}
                            </div>
                            <div className={`truncate text-gray-400 ${big ? "text-[0.9rem]" : "text-[0.78rem]"}`}>{event.detail}</div>
                        </div>
                        {event.kind !== "certificate" ? <span className="live-num shrink-0 text-[0.8rem] text-gray-500">{since(event.at, now)}</span> : null}
                    </li>
                );
            })}
        </ol>
    );
}

/** A quiet panel for the moments when there is nothing to show — which is the good case. */
export function Calm({ icon: Icon = Check, title, detail, className = "" }) {
    return (
        <div className={`flex flex-col items-center justify-center gap-2 text-center ${className}`}>
            <span className="flex size-[3rem] items-center justify-center rounded-full border border-white/10 text-gray-500">
                <Icon className="size-[1.3rem]" />
            </span>
            <div className="text-[0.95rem] text-gray-400">{title}</div>
            {detail ? <div className="text-[0.8rem] text-gray-600">{detail}</div> : null}
        </div>
    );
}

/** Certificates that somebody would receive expired. */
export function Certificates({ certificates }) {
    if (!certificates || certificates.expiring.length === 0) return null;
    return (
        <div className="flex flex-col gap-1.5">
            {certificates.expiring.slice(0, 3).map((cert) => (
                <div key={cert.domain} className="flex items-center gap-2 text-[0.85rem]">
                    <ShieldAlert className={`size-[1rem] shrink-0 ${cert.daysLeft < 0 ? "text-red-400" : "text-amber-400"}`} />
                    <span className="min-w-0 flex-1 truncate text-gray-300">{cert.domain}</span>
                    <span className={`live-num shrink-0 ${cert.daysLeft < 0 ? "text-red-400" : "text-amber-400"}`}>{cert.daysLeft < 0 ? `expired ${-cert.daysLeft}d` : `${cert.daysLeft}d`}</span>
                </div>
            ))}
        </div>
    );
}

